// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Resources } from '@shared/localizations/Resources';
import { TerceraQuickTreeEvents } from '../elements/QuickTree/TerceraQuickTree';
import NewsPanelTemplate from '@front/templates/NewsPanelTemplate.html';
import PanelSideBarBaseNoDataPartial from '@front/templates/PanelSideBarBaseNoDataPartial.html';
import { NewsItem } from '../cache/NewsItem';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { UrlUtils } from '@shared/utils/UrlUtils';
import { DynProperty } from '@shared/commons/DynProperty';
import { DataCache } from '@shared/commons/DataCache';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { NewsCache } from '@shared/commons/cache/NewsCache';
export class NewsPanel extends ApplicationPanelWithTable {
    constructor() {
        super(...arguments);
        this.unviewedNewsCount = 0; // количество непросмотренных новостей для хедера панели
        this.toolbarVisible = true; // видимость лейбла и комбобокса выбора Feed (новостного роута)
        this.selectedFeedValue = -1; // routeID выбранный в feedsComboBox
        this.callBackWas = false;
        this.initialLoadingStarted = false; // произошла ли начальная загрузка новостей (запустился ли LoadAllNews())
        this.feedsComboBoxTooltipLocalKey = 'panel.news.feedComboBox.tt';
        this.feedLabelLocalKey = 'panel.news.feed';
        this.headerLocaleKey = 'panel.news';
        this.rangeSelectPanel = null;
        this.currentRange = null;
        this.shownNewsIds = new Set();
        this.newsByRoute = new Map();
        this.deletedNewsByRoute = null; // сохраняем id новостей удаленных пользователем
        this.newsAbortController = null;
    }
    getType() {
        return PanelNames.NewsPanel;
    }
    oncomplete() {
        super.oncomplete();
        this.addRangeSelectPanel();
        this.populateTableContextMenu();
        this.createToolbar();
        this.subscribeToEvents();
        this.LoadAllNews();
        this.localize();
    }
    jbInit() {
        super.jbInit();
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        qt.AddToEnd = false;
        if (!this.callBackWas) {
            // если панель совсем новая, а не пришла из MongoDB
            qt.sortIndex = 0; // по умолчанию сортируем по нулевой колонке (Received - дата получения)
            qt.asc = false; // порядок сортировки по убыванию
        }
    }
    onAddNews(news) {
        if (isNullOrUndefined(news)) {
            return;
        }
        const routeID = news.GetRouteID();
        const newsID = news.GetID();
        if (this.checkIfNewsWasDeleted(routeID, newsID)) {
            return;
        }
        const routeNews = this.getOrCreateRouteNews(routeID);
        routeNews.push(news);
        if (this.isDailyMode() && routeNews.length > NewsPanel.MAX_NEWS_PER_ROUTE) {
            const removed = routeNews.shift();
            if (removed && this.isNewsInRange(removed)) {
                this.RemoveNewsEvent(removed.GetID());
            }
        }
        if (this.isNewsInRange(news)) {
            this.AddNewsEvent(news, false);
        }
    }
    addRangeSelectPanel() {
        const topPanel = this.Controls.newsPanelRangeSelect;
        topPanel.set({ name: 'topPanel' });
        topPanel.getReportEvent.Subscribe(this.getNewsReport, this);
        this.rangeSelectPanel = topPanel;
        topPanel.updateSelectedRange();
    }
    subscribeToEvents() {
        DataCache.NewsCache.addListener(this);
        if (!isNullOrUndefined(this.quickTableRactive)) {
            const qt = this.getQuickTable();
            if (!isNullOrUndefined(qt)) {
                qt.AfterEditItem.Subscribe(this.onLinkInContentClick, this);
            }
            this.quickTableRactive.on(TerceraQuickTreeEvents.DoubleClick, this.onDoubleClicked.bind(this));
        }
    }
    unsubscribeToEvents() {
        DataCache.NewsCache.removeListener(this);
        const qt = this.getQuickTable();
        if (!isNullOrUndefined(qt)) {
            qt.AfterEditItem.UnSubscribe(this.onLinkInContentClick, this);
        }
        if (this.newsAbortController) {
            this.newsAbortController.abort();
        }
        this.newsAbortController = null;
    }
    LoadAllNews() {
        this.initialLoadingStarted = true; // не добавляем айтемы раньше вызова LoadAllNews, чтоб была возможность использовать этот метод как для панели, что совершает подписку так и для последующих без лишних созданий айтемов #99505
        const newsToAdd = this.prepareNews(NewsPanel.ALL_FEEDS_CB_ITEM_VALUE);
        for (const news of newsToAdd) {
            this.AddNewsEvent(news, true);
        }
        const qt = this.getQuickTable();
        if (!isNullOrUndefined(qt)) {
            qt.sortRowsArray();
        } // #99511
    }
    AfterLoad() {
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        qt.ClearAll();
        this.unviewedNewsCount = 0;
        this.shownNewsIds.clear();
        this.LoadAllNews();
    }
    getNewsReport(startTime, finishTime) {
        const routes = Array.from(DataCache.NewsCache.NewsRoutes.keys());
        if (routes.length === 0) {
            return;
        }
        if (this.newsAbortController) {
            this.newsAbortController.abort();
        }
        void this.set('loading', true);
        this.newsAbortController = new AbortController();
        const from = new Date(startTime);
        const to = new Date(finishTime);
        this.currentRange = { from, to };
        (() => __awaiter(this, void 0, void 0, function* () {
            try {
                for (const routeID of routes) {
                    yield DataCache.NewsCache.requestNews(from, to, routeID, NewsPanel.NEWS_REQUEST_NUMBER, this.newsAbortController.signal);
                }
                this.AfterLoad();
            }
            finally {
                void this.set('loading', false);
            }
        }))();
    }
    AddNewsEvent(news, noNeedToSort) {
        if (isNullOrUndefined(news) || !this.initialLoadingStarted) {
            return;
        }
        if (this.shownNewsIds.has(news.GetID())) {
            return;
        }
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        const routeID = news.GetRouteID();
        const rowIsVisible = this.selectedFeedValue === NewsPanel.ALL_FEEDS_CB_ITEM_VALUE || this.selectedFeedValue === routeID;
        if (!rowIsVisible)
            return;
        const newsWasViewed = NewsCache.isNewsReaded(news);
        qt.AddItem(new NewsItem(news, newsWasViewed));
        this.shownNewsIds.add(news.GetID());
        if (!noNeedToSort) {
            qt.sortRowsArray();
        } // #99511
        if (!newsWasViewed) {
            this.unviewedNewsCount++;
        }
        if (this.get('noData') && qt.rowsArray.length > 0) {
            void this.set('noData', false);
        }
    }
    RemoveNewsEvent(newsID) {
        // это удаление происходит, когда новая новость вытесняет старую по их максимальному кол-ву на роут
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        const rowsArray = qt.rowsArray;
        if (!isValidArray(rowsArray))
            return;
        for (const row of rowsArray) {
            const item = row === null || row === void 0 ? void 0 : row.item;
            if ((item === null || item === void 0 ? void 0 : item.getNewsID()) === newsID) {
                qt.RemoveItem(item.ItemId);
                this.shownNewsIds.delete(newsID);
                if (!item.Disabled) {
                    this.unviewedNewsCount--;
                }
                return;
            }
        }
    }
    dispose() {
        this.rangeSelectPanel.getReportEvent.UnSubscribe(this.getNewsReport, this);
        this.unsubscribeToEvents();
        super.dispose();
    }
    updatePanelHeader() {
        void this.set({ header: Resources.getResource(this.headerLocaleKey) + '  (' + this.unviewedNewsCount + ')' });
    }
    onLinkInContentClick(info) {
        let url = null;
        const item = !isNullOrUndefined(info === null || info === void 0 ? void 0 : info.row) ? info.row.item : null;
        if (!isNullOrUndefined(item)) {
            url = item.getLinkInContent();
        }
        UrlUtils.openFixedURL(url);
    }
    onSelectedFeedChange(_el, cbItem) {
        var _a;
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt) || isNullOrUndefined(cbItem)) {
            return;
        }
        qt.ClearAll();
        this.shownNewsIds.clear();
        const value = (_a = cbItem.value) !== null && _a !== void 0 ? _a : cbItem;
        this.selectedFeedValue = value;
        this.unviewedNewsCount = 0;
        void this.set('noData', true);
        const newsToAdd = this.prepareNews(value);
        if (isValidArray(newsToAdd)) {
            for (const news of newsToAdd) {
                this.AddNewsEvent(news, true);
            }
            qt.sortRowsArray();
        }
    }
    prepareNews(value) {
        var _a;
        let newsToAdd = [];
        const isDaily = this.isDailyMode();
        const newsByRoute = this.newsByRoute;
        const pushFiltered = (routeID, arr) => {
            const filtered = arr.filter((n) => !this.checkIfNewsWasDeleted(routeID, n.GetID()) && this.isNewsInRange(n));
            newsToAdd = newsToAdd.concat(this.limitateNewsNumber(isDaily, filtered));
        };
        if (value === NewsPanel.ALL_FEEDS_CB_ITEM_VALUE) {
            for (const [routeID, routeNews] of newsByRoute) {
                pushFiltered(routeID, routeNews);
            }
        }
        else {
            const routeNews = (_a = newsByRoute.get(value)) !== null && _a !== void 0 ? _a : [];
            pushFiltered(value, routeNews);
        }
        return newsToAdd;
    }
    limitateNewsNumber(isDaily, arr = []) {
        if (isDaily && arr.length > NewsPanel.MAX_NEWS_PER_ROUTE) {
            return arr.slice(-NewsPanel.MAX_NEWS_PER_ROUTE);
        }
        return arr;
    }
    createToolbar() {
        this.fillFeedsComboBox();
        this.on('onSelectedFeedChange', this.onSelectedFeedChange);
        this.ShowToolbarStateUpdate(this.toolbarVisible);
        this.layoutTable();
    }
    fillFeedsComboBox() {
        const dc = DataCache;
        const newsRouteIds = Array.from(dc.NewsCache.NewsRoutes.keys());
        if (newsRouteIds.length === 0) {
            return;
        }
        const itemAllFeeds = {
            type: NewContextMenuItemType.Default,
            text: 'panel.news.all',
            value: NewsPanel.ALL_FEEDS_CB_ITEM_VALUE
        };
        let selectedCBItem = this.selectedFeedValue === itemAllFeeds.value ? itemAllFeeds : null;
        let items = [];
        for (const routeID of newsRouteIds) {
            const route = dc.Routes.getRouteById(routeID);
            if (!isNullOrUndefined(route)) {
                const item = {
                    type: NewContextMenuItemType.Default,
                    text: route.Name,
                    value: routeID
                };
                if (this.selectedFeedValue === item.value) {
                    selectedCBItem = item;
                }
                items.push(item);
            }
        }
        const feedsComboBox = !isNullOrUndefined(this.Controls) ? this.Controls.feedsComboBox : null;
        if (isNullOrUndefined(feedsComboBox))
            return;
        items.sort(function (a, b) {
            // #99501
            const s1 = a.text.toUpperCase();
            const s2 = b.text.toUpperCase();
            if (s1 === s2)
                return 0;
            return s1 > s2 ? 1 : -1;
        });
        items = [itemAllFeeds].concat(items);
        void feedsComboBox.set({ items });
        if (!isNullOrUndefined(selectedCBItem)) {
            void feedsComboBox.set('selectedItem', selectedCBItem);
        }
    }
    localize() {
        super.localize();
        void this.set('noDataText', Resources.getResource('panel.news.noData'));
        const feedsLabel = !isNullOrUndefined(this.Controls) ? this.Controls.feedsLabel : null;
        if (!isNullOrUndefined(feedsLabel)) {
            void feedsLabel.set('text', Resources.getResource(this.feedLabelLocalKey));
        }
        const feedsComboBox = !isNullOrUndefined(this.Controls) ? this.Controls.feedsComboBox : null;
        if (!isNullOrUndefined(feedsComboBox)) {
            void feedsComboBox.set('tooltip', Resources.getResource(this.feedsComboBoxTooltipLocalKey));
        }
    }
    onDoubleClicked() {
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        const selectedOrderIdArr = qt.selectedRowIds;
        const qtRow = qt.getItemById(selectedOrderIdArr[0]);
        const item = !isNullOrUndefined(qtRow) ? qtRow.item : null;
        if (!isNullOrUndefined(item)) {
            if (!item.Disabled) {
                // новость не была прежде прочитана -> следует обновить header
                this.unviewedNewsCount--;
                this.updatePanelHeader();
            }
            item.Disabled = true;
            DataCache.NewsCache.markNewsAsRead(item.news);
            item.OnNewsDoubleClicked();
        }
    }
    onRemoveAll() {
        // удаление пользователем через пункт контекстного меню Clear table
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        this.unviewedNewsCount = 0;
        const rows = qt.rowsArray;
        for (const element of rows) {
            this.onRowWasDeletedByUser(element);
        }
        qt.ClearAll();
        this.shownNewsIds.clear();
        if (qt.rowsArray.length === 0) {
            void this.set('noData', true);
        }
    }
    onRemoveSelected() {
        // удаление пользователем через пункт контекстного меню Remove
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        const selectedRowIds = qt.selectedRowIds.slice();
        for (const rowID of selectedRowIds) {
            const row = qt.getItemById(rowID);
            const item = row === null || row === void 0 ? void 0 : row.item;
            if (item && !item.Disabled) {
                this.unviewedNewsCount--;
            }
            if (item) {
                this.shownNewsIds.delete(item.getNewsID());
            }
            this.onRowWasDeletedByUser(row);
            qt.RemoveRow(rowID);
        }
        if (qt.rowsArray.length === 0) {
            void this.set('noData', true);
        }
    }
    onRowWasDeletedByUser(row) {
        if (isNullOrUndefined(row === null || row === void 0 ? void 0 : row.item)) {
            return;
        }
        const item = row.item;
        const newsID = item.getNewsID();
        const routeID = item.getRouteID();
        if (isNullOrUndefined(this.deletedNewsByRoute)) {
            this.deletedNewsByRoute = {};
        }
        if (isNullOrUndefined(this.deletedNewsByRoute[routeID])) {
            this.deletedNewsByRoute[routeID] = [];
        }
        this.deletedNewsByRoute[routeID].push(newsID);
    }
    checkIfNewsWasDeleted(routeID, newsID) {
        // была ли новость удалена пользователем из таблицы
        if (isNullOrUndefined(this.deletedNewsByRoute)) {
            return false;
        }
        const deletedNewsIDs = this.deletedNewsByRoute[routeID];
        return isValidArray(deletedNewsIDs) && deletedNewsIDs.includes(newsID);
    }
    preparePopup() {
        super.preparePopup();
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        this.menuTagDict[NewsPanel.CLEAR_TABLE_CONTEXT_MENU_TAG].enabled = qt.visibleRowCount() > 0;
        const selectedRowIds = qt.selectedRowIds;
        this.menuTagDict[NewsPanel.REMOVE_ONE_MENU_TAG].enabled = isValidArray(selectedRowIds);
    }
    populateTableContextMenu() {
        const items = [];
        const moreThanOnFeed = Array.from(DataCache.NewsCache.NewsRoutes.keys()).length > 1;
        this.AddExportContextMenuItem(items);
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.positions.menu.View',
            tag: 'View',
            svgImage: 'View',
            subitems: [
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.news.menu.ShowToolbar',
                    checked: this.toolbarVisible,
                    visible: !Resources.isHidden('panel.news.menu.ShowToolbar'),
                    enabled: moreThanOnFeed,
                    canCheck: true,
                    event: this.ShowToolbarStateChange.bind(this)
                }
            ]
        });
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.news.menu.clearAll',
            event: this.onRemoveAll.bind(this),
            tag: NewsPanel.CLEAR_TABLE_CONTEXT_MENU_TAG
        });
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.news.menu.removeOneRow',
            event: this.onRemoveSelected.bind(this),
            enabled: false,
            tag: NewsPanel.REMOVE_ONE_MENU_TAG
        });
        this.menuTagDict = createTagDictionary(items);
        this.getQuickTable().setTableContextMenuItems(items);
    }
    ShowToolbarStateChange(menuItem) {
        this.toolbarVisible = menuItem.checked;
        this.ShowToolbarStateUpdate(this.toolbarVisible);
    }
    ShowToolbarStateUpdate(state) {
        const feedsLabel = this.Controls.feedsLabel;
        const feedsComboBox = this.Controls.feedsComboBox;
        const routes = !isNullOrUndefined(feedsComboBox) ? feedsComboBox.get('items') : null;
        const routesNum = isValidArray(routes) ? routes.length : 0;
        if (routesNum < 3) {
            // ALL + one route -> need to hide
            state = false;
        }
        void feedsComboBox.set('visible', state);
        void feedsLabel.set('visible', state);
        const leftRangePanel = state ? feedsComboBox.get('left') + feedsComboBox.get('width') + 5 : 5;
        const rangePanel = this.rangeSelectPanel;
        void rangePanel.set({ left: leftRangePanel });
        this.layoutTable();
    }
    Properties() {
        const properties = super.Properties();
        properties.push(new DynProperty('toolbarVisible', this.toolbarVisible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP), new DynProperty('selectedFeed', this.selectedFeedValue, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP), new DynProperty('deletedNewsByRoute', JSON.stringify(this.deletedNewsByRoute), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        this.toolbarVisible = DynProperty.getPropValue(newProperties, 'toolbarVisible');
        this.selectedFeedValue = DynProperty.getPropValue(newProperties, 'selectedFeed');
        const deletedNewsByRouteProp = DynProperty.getPropValue(newProperties, 'deletedNewsByRoute');
        if (!isNullOrUndefined(deletedNewsByRouteProp)) {
            this.deletedNewsByRoute = JSON.parse(deletedNewsByRouteProp);
        }
        this.callBackWas = true;
    }
    isDailyMode() {
        return !isNullOrUndefined(this.rangeSelectPanel) && this.rangeSelectPanel.isDaily();
    }
    isNewsInRange(news) {
        if (!this.currentRange)
            return true;
        const dt = news.GetDate();
        return dt >= this.currentRange.from && dt <= this.currentRange.to;
    }
    getOrCreateRouteNews(routeID) {
        let routeNews = this.newsByRoute.get(routeID);
        if (!routeNews) {
            routeNews = [];
            this.newsByRoute.set(routeID, routeNews);
        }
        return routeNews;
    }
}
NewsPanel.NEWS_REQUEST_NUMBER = 1000; // Запрашиваемое количество новостей в панеле = 1000 штук по каждому новостному роуту - хардкод. Каждая последующая новость которая приходит в клиент в реал-тайм, перезаписывает более старую.
NewsPanel.MAX_NEWS_PER_ROUTE = 100;
NewsPanel.CLEAR_TABLE_CONTEXT_MENU_TAG = 'REMOVE_ALL_MENU_TAG';
NewsPanel.REMOVE_ONE_MENU_TAG = 'REMOVE_ONE_MENU_TAG';
NewsPanel.ALL_FEEDS_CB_ITEM_VALUE = -1;
ApplicationPanelWithTable.extendWith(NewsPanel, {
    partials: {
        bodyPartial: NewsPanelTemplate,
        noDataPart: PanelSideBarBaseNoDataPartial
    },
    data: () => {
        return {
            noData: true,
            noDataText: '',
            noDataImg: 'news',
            canLinkByAccount: false,
            isAccountLinkShow: false,
            isSymbolLinkShow: false
        };
    }
});
